import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useTable } from "@/lib/store";
import { Card, Kicker, Chip, Field, Area, Pick, Empty, Avatar } from "@/components/kit";
import { fmtDate } from "@/lib/util";
import { ArrowLeft, Upload, Download, Trash2, FileText } from "lucide-react";

const STATUSES = ["Not started", "In progress", "Submitted", "Confirmed"];

const statusColour = (s) =>
  s === "Confirmed" ? "var(--green)" : s === "Submitted" ? "var(--brass-bright)"
    : s === "In progress" ? "var(--amber)" : undefined;

export default function CupCriterion() {
  const { id } = useParams();
  const { me, yearId, memberById, canWrite } = useTable();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");
  const [points, setPoints] = useState("");
  const [deadline, setDeadline] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cup/criteria/${id}`);
      setC(data);
      setNotes(data.notes || "");
      setPoints(data.pointsClaimed ?? "");
      setDeadline((data.deadline || "").slice(0, 10));
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <Empty line="Loading the record…" />;

  const mine = [...(c.ownerIds || []), ...(c.supportIds || [])].includes(me.memberId);
  const canEdit = canWrite && (me.isAdmin || mine);

  const save = async (patch, quiet) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/cup/criteria/${c.id}`, patch);
      setC(data);
      if (!quiet) toast.success("Recorded.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setBusy(true);
    try {
      await api.post(`/cup/criteria/${c.id}/evidence`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success("Added to the vault.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const removeFile = async (f) => {
    try {
      await api.delete(`/cup/evidence/${f.id}`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/cup/evidence/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 stagger" data-testid="cup-criterion-page">
      <Link to="/cup" className="inline-flex items-center gap-1.5 text-sm" data-testid="cup-back"
        style={{ color: "var(--ink-mute)" }}>
        <ArrowLeft size={14} /> The Forum Cup
      </Link>

      <header>
        <div className="kicker mb-1.5">Criterion {c.num} · {c.maxPoints} points</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{c.name}</h1>
        <p className="lede mt-3 max-w-2xl" data-testid="cup-rule">{c.rule}</p>
      </header>

      {/* ------------------------------------------------- the record */}
      <Card className="p-5" data-testid="cup-record">
        <Kicker>The record</Kicker>
        <div className="grid sm:grid-cols-2 gap-4 mt-3">
          <Pick
            label="Status"
            value={c.status}
            options={STATUSES}
            disabled={!canEdit || busy}
            data-testid="cup-status-pick"
            onChange={(e) => save({ status: e.target.value })}
          />
          <div className="flex gap-2 items-end">
            <Field
              label={`Points claimed (of ${c.maxPoints})`}
              value={points}
              disabled={!canEdit}
              placeholder="auto"
              data-testid="cup-points-input"
              onChange={(e) => setPoints(e.target.value.replace(/[^\d.]/g, ""))}
            />
            {canEdit && (
              <button className="btn-quiet !min-h-0 !py-2.5 mb-1" disabled={busy}
                data-testid="cup-points-save"
                onClick={() => save({ pointsClaimed: points })}>Set</button>
            )}
          </div>
          <div className="flex gap-2 items-end">
            <Field
              label="Deadline"
              type="date"
              value={deadline}
              disabled={!canEdit}
              data-testid="cup-deadline-input"
              onChange={(e) => setDeadline(e.target.value)}
            />
            {canEdit && (
              <button className="btn-quiet !min-h-0 !py-2.5 mb-1" disabled={busy}
                data-testid="cup-deadline-save"
                onClick={() => save({ deadline })}>Move</button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Chip color={statusColour(c.status)} testId="cup-status-chip">{c.status}</Chip>
          <Chip testId="cup-points-chip">{c.awarded} of {c.maxPoints} points</Chip>
          {c.deadline && (
            <Chip color={c.missed ? "var(--rust)" : c.atRisk ? "var(--amber)" : undefined}
              testId="cup-deadline-chip">
              {fmtDate(c.deadline)}{c.daysLeft !== null ? ` · ${c.daysLeft} days` : ""}
            </Chip>
          )}
          {(c.ownerIds || []).map((mid) => {
            const m = memberById(mid);
            return m ? (
              <span key={mid} className="inline-flex items-center gap-1.5 text-xs"
                style={{ color: "var(--ink-mute)" }}>
                <Avatar name={m.name} color={m.avatarColor} size={18} />
                {m.name.split(" ")[0]}
              </span>
            ) : null;
          })}
        </div>

        {c.progress && (
          <p className="text-xs mt-4" style={{ color: "var(--ink-mute)" }} data-testid="cup-progress">
            {c.progress.done} of {c.progress.need} {c.progress.label}
          </p>
        )}
      </Card>

      {/* ------------------------------------------------- notes */}
      <Card className="p-5" data-testid="cup-notes">
        <Kicker>What has happened so far</Kicker>
        <p className="text-xs mt-2 mb-3" style={{ color: "var(--ink-mute)" }}>
          Write it as you would tell the Forum Officer. This is what prints on the report.
        </p>
        <Area
          value={notes}
          rows={6}
          disabled={!canEdit}
          data-testid="cup-notes-input"
          placeholder="Dates, names, what was agreed, what is left to do…"
          onChange={(e) => setNotes(e.target.value)}
        />
        {canEdit && (
          <button className="btn-ink mt-3" disabled={busy || notes === (c.notes || "")}
            data-testid="cup-notes-save" onClick={() => save({ notes })}>Save the record</button>
        )}
      </Card>

      {/* ------------------------------------------------- evidence */}
      <Card className="p-5" data-testid="cup-evidence">
        <Kicker>Files, if you have any</Kicker>
        <p className="text-xs mt-2" style={{ color: "var(--ink-mute)" }}>
          The Cup runs on trust — no proof is required. Keep anything here you may want later.
        </p>
        {canWrite && (
          <label className="btn-quiet mt-4 inline-flex cursor-pointer" data-testid="evidence-upload">
            <Upload size={13} /> Add a file
            <input type="file" className="hidden" disabled={busy}
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        )}
        <div className="mt-4 space-y-2">
          {(c.evidence || []).length === 0 ? (
            <p className="text-sm" style={{ color: "var(--ink-mute)" }}>Nothing filed.</p>
          ) : (
            c.evidence.map((f) => (
              <div key={f.id} className="flex flex-wrap items-center gap-3 py-2 border-t"
                style={{ borderColor: "var(--hair)" }} data-testid={`evidence-${f.id}`}>
                <FileText size={14} style={{ color: "var(--brass)" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{f.name}</div>
                  <div className="text-[11.5px]" style={{ color: "var(--ink-mute)" }}>
                    {f.uploadedBy} · {fmtDate(f.createdAt)}
                  </div>
                </div>
                <button className="btn-quiet !min-h-0 !px-3 !py-2" data-testid={`evidence-download-${f.id}`}
                  onClick={() => download(f)}><Download size={13} /></button>
                {(me.isAdmin || f.uploadedById === me.memberId) && canWrite && (
                  <button className="btn-quiet !min-h-0 !px-3 !py-2" data-testid={`evidence-delete-${f.id}`}
                    onClick={() => removeFile(f)}><Trash2 size={13} /></button>
                )}
              </div>
            ))
          )}
        </div>
      </Card>

      {(c.log || []).length > 0 && (
        <Card className="p-5" data-testid="cup-log">
          <Kicker>Activity</Kicker>
          <div className="mt-3 space-y-1.5">
            {c.log.slice(0, 12).map((l) => (
              <p key={l.id} className="text-[12.5px]" style={{ color: "var(--ink-mute)" }}>
                {fmtDate(l.at)} · {l.by} — {l.summary}
              </p>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
